import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AlreadyCheckedInError,
  CentroVisit,
  CheckInInput,
  ListRecentCentroVisitsResponse,
} from 'shared';
import { ApiClientError, api, type MaybeEmpty } from '@/lib/api';
import { useAuthStore } from '@/stores/auth';

const BASE = '/api/centros';

export const centroVisitKeys = {
  current: ['centros', 'current'] as const,
  recent: (limit: number) => ['centros', 'recent', limit] as const,
};

/**
 * Type guard for the structured 409 body emitted by POST /api/centros/check-in
 * when the participant already has an active visit.
 */
export function isAlreadyCheckedIn(err: unknown): err is ApiClientError & {
  body: AlreadyCheckedInError;
} {
  if (!(err instanceof ApiClientError) || err.status !== 409) return false;
  const body = err.body as Partial<AlreadyCheckedInError> | null;
  return !!body && body.error === 'AlreadyCheckedIn' && !!body.activeVisit;
}

/**
 * GET /api/centros/current — returns the participant's open visit, or null
 * when they are not checked into any centro (API returns 204).
 *
 * `staleTime` defaults to 30s; screens that need backend changes to surface
 * quickly (Início, Reservas) pass a shorter window so the next focus/navigation
 * refetch picks up out-of-band updates.
 */
export function useCurrentCentro(staleTime = 30_000) {
  const { isAuthenticated, kind } = useAuthStore();
  return useQuery({
    queryKey: centroVisitKeys.current,
    queryFn: () => api.get<MaybeEmpty<CentroVisit>>(`${BASE}/current`),
    enabled: isAuthenticated && kind === 'participant',
    staleTime,
  });
}

/** GET /api/centros/recent?limit=… */
export function useRecentCentros(limit = 5) {
  const { isAuthenticated, kind } = useAuthStore();
  return useQuery({
    queryKey: centroVisitKeys.recent(limit),
    queryFn: () =>
      api.get<ListRecentCentroVisitsResponse>(`${BASE}/recent?limit=${limit}`),
    enabled: isAuthenticated && kind === 'participant',
  });
}

/** POST /api/centros/check-in — surfaces 409 via `isAlreadyCheckedIn(err)`. */
export function useCheckInCentro() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CheckInInput) => api.post<CentroVisit>(`${BASE}/check-in`, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['centros'] });
    },
  });
}

/** POST /api/centros/check-out. */
export function useCheckOutCentro() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<CentroVisit>(`${BASE}/check-out`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['centros'] });
    },
  });
}
